import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, MenuItem, Grid, IconButton, Tooltip } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Controller, Control } from "react-hook-form";

import {
  addVoyageFilterValue,
  removeSelectedTerminalCallFilterValue,
} from "../../data/filter";

import { selectedFilteredVoyage } from "../../selectors/filterSelector";
import { useVoyageTerminalCalls } from "../hooks/useVoyageTerminalCalls";
import { useVoyageContainers } from "../hooks/useVoyageContainers";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData";
import { showModal } from "../../data/modal";

interface Props {
  includeContainers?: boolean;
  allowAdd?: boolean;
  allowRefresh?: boolean;
  control?: Control<any>;
  name?: string;
}

const normalizeCollection = (value: any) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const normalizeApiId = (value: any) => {
  if (!value) return null;
  if (typeof value === "string") return value;

  return value["@id"] ?? null;
};

const normalizeNumericId = (value: any) => {
  const apiId = normalizeApiId(value);

  if (apiId) {
    return apiId.split("/").filter(Boolean).pop();
  }

  if (value?.id !== null && value?.id !== undefined) {
    return String(value.id);
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value.split("/").filter(Boolean).pop();
  }

  return "";
};

const VoyagePicker: React.FC<Props> = ({
  allowAdd = true,
  allowRefresh = false,
  includeContainers = false,
  control,
  name = "voyage",
}) => {
  const dispatch = useDispatch();
  const refreshVoyageData = useRefreshVoyageData();

  const voyageData = useSelector((state: any) => state.voyages.data);
  const selectedTerminalCallsState = useSelector(
    (state: any) => state.selectedTerminalCalls
  );
  const { voyage } = useSelector(selectedFilteredVoyage);

  const [refreshingTerminalCalls, setRefreshingTerminalCalls] = useState(false);

  useEffect(() => {
    if (!voyage && !control) {
      const savedVoyageId = localStorage.getItem("selectedVoyageId");

      if (savedVoyageId) {
        dispatch(addVoyageFilterValue(savedVoyageId));
      }
    }
  }, [dispatch, voyage, control]);

  useEffect(() => {
    if (voyage?.["@id"] && !control) {
      localStorage.setItem("selectedVoyageId", voyage["@id"]);
    }
  }, [voyage, control]);

  useVoyageTerminalCalls(control ? undefined : voyage);
  useVoyageContainers(control && !includeContainers ? null : voyage);

  const sortedVoyages = voyageData
    .slice()
    .sort((a: any, b: any) => {
      const hasA = !!a.startDate;
      const hasB = !!b.startDate;

      if (hasA && hasB) {
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      }

      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;

      return 0;
    });

  const terminalCalls = useMemo(() => {
    return normalizeCollection(selectedTerminalCallsState?.data);
  }, [selectedTerminalCallsState]);

  const refreshBaseTerminalCall = useMemo(() => {
    return terminalCalls.find((terminalCall: any) => normalizeNumericId(terminalCall));
  }, [terminalCalls]);

  const canRefreshTerminalCalls = Boolean(
    allowRefresh &&
      !control &&
      voyage &&
      refreshBaseTerminalCall &&
      !refreshingTerminalCalls
  );

  const refreshDisabledReason = !voyage
    ? "Select a voyage first"
    : !refreshBaseTerminalCall
    ? "No terminal calls found for this voyage"
    : "Refresh terminal calls";

  const handleManualRefresh = async () => {
    if (!canRefreshTerminalCalls) {
      return;
    }

    const terminalCallId = normalizeNumericId(refreshBaseTerminalCall);

    if (!terminalCallId) {
      return;
    }

    try {
      setRefreshingTerminalCalls(true);

      const response = await fetch(
        `/api/terminal_call/${terminalCallId}/recalculate-voyage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => null);

        throw new Error(
          error?.message || "Failed to refresh voyage terminal calls."
        );
      }

      if (voyage) {
        await refreshVoyageData(voyage);
      }
    } finally {
      setRefreshingTerminalCalls(false);
    }
  };

  const renderSelect = (value: string, onChange: (value: string) => void) => (
    <Select
      fullWidth
      size="small"
      displayEmpty
      value={value}
      onChange={(e: SelectChangeEvent<string>) => {
        const id = e.target.value;

        onChange(id);
        dispatch(removeSelectedTerminalCallFilterValue());
        dispatch(addVoyageFilterValue(id));
        localStorage.setItem("selectedVoyageId", id);
      }}
      placeholder="Voyage"
    >
      {sortedVoyages.map((v: any) => (
        <MenuItem key={v["@id"]} value={v["@id"]}>
          {v.name}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid item xs>
        {control ? (
          <Controller
            name={name}
            control={control}
            defaultValue=""
            render={({ field }) => renderSelect(field.value, field.onChange)}
          />
        ) : (
          renderSelect(voyage?.["@id"] ?? "", () => {})
        )}
      </Grid>

      {allowRefresh && (
        <Grid item>
          <Tooltip
            title={
              refreshingTerminalCalls
                ? "Refreshing terminal calls..."
                : refreshDisabledReason
            }
          >
            <span>
              <IconButton
                size="small"
                aria-label="Refresh"
                onClick={handleManualRefresh}
                disabled={!canRefreshTerminalCalls}
                sx={{
                  color: refreshingTerminalCalls ? "#1976d2" : "inherit",
                  backgroundColor: refreshingTerminalCalls
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  transition: "background-color 140ms ease, color 140ms ease",
                  "&:hover": {
                    color: "#1976d2",
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                  },
                  "&.Mui-disabled": {
                    color: refreshingTerminalCalls
                      ? "#1976d2"
                      : "rgba(0, 0, 0, 0.26)",
                  },
                }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    color: refreshingTerminalCalls ? "#1976d2" : "inherit",
                    animation: refreshingTerminalCalls
                      ? "voyage-terminal-refresh-spin 0.8s linear infinite"
                      : "none",
                    "@keyframes voyage-terminal-refresh-spin": {
                      from: {
                        transform: "rotate(0deg)",
                      },
                      to: {
                        transform: "rotate(360deg)",
                      },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      )}

      {allowAdd && (
        <Grid item>
          <Tooltip title="Create a voyage">
            <IconButton
              size="small"
              onClick={() =>
                dispatch(
                  showModal({
                    title: `Voyage creation`,
                    description: "Add voyage",
                    form: "voyageform",
                  })
                )
              }
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      )}
    </Grid>
  );
};

export default VoyagePicker;